/**
 * Engagement Letter 页面
 *
 * 功能：
 * - S&P信息配置（区域、邮箱）
 * - 委托协议模板选择
 * - 文档生成和预览
 * - 多方签署（Bravowhale、Issuer）
 * - 发送给S&P
 * - S&P签署记录（邮件接收）
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Steps,
  Form,
  Input,
  Select,
  Button,
  Space,
  Descriptions,
  Alert,
  Modal,
  message,
  Spin,
  Timeline,
  Divider,
  Row,
  Col,
  Typography,
  Upload,
  Empty,
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  SendOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  MailOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { SignaturePad } from '../../components/spo';
import {
  useGetSPOQuery,
  useGenerateELMutation,
  useSignELMutation,
  useSendELToSPMutation,
  useRecordSPSignatureMutation,
  useGetELStatusQuery,
} from '../../store/api/spoApi';
import { UnifiedStatusTag } from '../../components/unified/UnifiedStatusTag';
import type { ELStatus } from '../../types/spo';
import { useResolvedSpoId } from './hooks/useResolvedSpoId';

const { Step } = Steps;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Paragraph, Text } = Typography;

interface EngagementLetterPageProps {
  spoId?: string;
  projectId?: string;
}

export const EngagementLetterPage: React.FC<EngagementLetterPageProps> = ({
  spoId: propSpoId,
  projectId: propProjectId,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const { resolvedSpoId, isResolving, isResolveError } = useResolvedSpoId({
    projectId: propProjectId,
    spoId: propSpoId,
  });

  // State
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showRecordSPSignModal, setShowRecordSPSignModal] = useState(false);
  const [signatureType, setSignatureType] = useState<'bravowhale' | 'issuer'>(
    'bravowhale'
  );
  const [signatureData, setSignatureData] = useState<string>(''); // 签名Base64数据

  // API
  const {
    data: spoResponse,
    isLoading,
    isError,
  } = useGetSPOQuery(resolvedSpoId!, { skip: !resolvedSpoId });
  const { data: elStatusResponse } = useGetELStatusQuery(resolvedSpoId!, {
    skip: !resolvedSpoId,
  });
  const [generateEL, { isLoading: isGenerating }] = useGenerateELMutation();
  const [signEL, { isLoading: isSigning }] = useSignELMutation();
  const [sendELToSP, { isLoading: isSending }] = useSendELToSPMutation();
  const [recordSPSignature, { isLoading: isRecording }] =
    useRecordSPSignatureMutation();

  const spoData = spoResponse?.data;
  const elData = spoData?.engagementLetter;
  const elStatus = elData?.status || 'PENDING_GENERATION';

  // 计算当前步骤
  const currentStep = useMemo(() => {
    if (!elData) return 0;
    switch (elStatus) {
      case 'PENDING_GENERATION':
        return 0;
      case 'BRAVOWHALE_SIGNED':
        return 1;
      case 'ISSUER_SIGNED':
        return 2;
      case 'SENT_TO_SP':
        return 3;
      case 'SIGNED_BY_SP':
      case 'PAYMENT_PENDING':
      case 'PAYMENT_MADE':
        return 4;
      default:
        return 0;
    }
  }, [elStatus, elData]);

  // 生成委托协议
  const handleGenerateEL = async () => {
    try {
      const values = await form.validateFields([
        'spRegion',
        'spAnalystEmail',
        'elTemplateId',
        'estimatedFee',
      ]);
      await generateEL({
        spoId: resolvedSpoId!,
        data: values,
      }).unwrap();
      message.success('委托协议生成成功！');
    } catch (error: any) {
      message.error(error?.data?.error?.message || '生成失败，请重试');
    }
  };

  // 签署委托协议
  const handleSignEL = async () => {
    try {
      const values = await form.validateFields(['agreementConfirmed']);
      if (!values.agreementConfirmed) {
        message.warning('请确认同意协议条款');
        return;
      }

      if (!signatureData) {
        message.warning('请先完成手写签名');
        return;
      }

      await signEL({
        spoId: resolvedSpoId!,
        data: {
          signatureType,
          agreementConfirmed: true,
          signatureData, // 添加签名数据
        },
      }).unwrap();

      message.success(
        `${signatureType === 'bravowhale' ? 'Bravowhale' : '发行人'}签署成功！`
      );
      setShowSignModal(false);
      setSignatureData(''); // 清空签名数据
    } catch (error: any) {
      message.error(error?.data?.error?.message || '签署失败，请重试');
    }
  };

  // 发送给S&P
  const handleSendToSP = async () => {
    try {
      await sendELToSP(resolvedSpoId!).unwrap();

      message.success('委托协议已发送给S&P！');
      setShowSendModal(false);
    } catch (error: any) {
      message.error(error?.data?.error?.message || '发送失败，请重试');
    }
  };

  // 记录S&P签署
  const handleRecordSPSignature = async () => {
    try {
      const values = await form.validateFields([
        'spSignatureProofUrl',
        'spSignedDate',
      ]);
      await recordSPSignature({
        spoId: resolvedSpoId!,
        data: values,
      }).unwrap();

      message.success('S&P签署记录已保存！');
      setShowRecordSPSignModal(false);
    } catch (error: any) {
      message.error(error?.data?.error?.message || '记录失败，请重试');
    }
  };

  // 预览文档
  const handlePreview = () => {
    if (!elData?.elDocumentUrl) {
      message.warning('文档尚未生成');
      return;
    }
    setShowPreviewModal(true);
  };

  // 下载文档
  const handleDownload = () => {
    if (!elData?.elDocumentUrl) {
      message.warning('文档尚未生成');
      return;
    }
    window.open(elData.elDocumentUrl, '_blank');
  };

  // 渲染加载状态
  if (isLoading || isResolving) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 12 }}>加载委托协议数据...</div>
      </div>
    );
  }

  // 渲染错误状态
  if (isError || isResolveError || !spoData || !resolvedSpoId) {
    return (
      <Alert
        message="加载失败"
        description="委托协议数据加载失败，请刷新页面重试"
        type="error"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* 页面标题 */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Phase 1: 委托协议 (Engagement Letter)</Title>
        <Paragraph type="secondary">
          与S&P Global签署委托协议，明确SPO评估的范围、费用和时间表
        </Paragraph>
        <Space>
          <Button onClick={() => navigate(`/spo/${resolvedSpoId}`)}>
            返回SPO流程
          </Button>
        </Space>
      </div>

      {/* 流程步骤 */}
      <Card style={{ marginBottom: 24 }}>
        <Steps current={currentStep}>
          <Step
            title="生成协议"
            description="配置S&P信息"
            icon={<FileTextOutlined />}
            status={
              currentStep > 0
                ? 'finish'
                : currentStep === 0
                  ? 'process'
                  : 'wait'
            }
          />
          <Step
            title="Bravowhale签署"
            description="运营商签署"
            icon={<CheckCircleOutlined />}
            status={
              currentStep > 1
                ? 'finish'
                : currentStep === 1
                  ? 'process'
                  : 'wait'
            }
          />
          <Step
            title="发行人签署"
            description="Issuer签署"
            icon={<CheckCircleOutlined />}
            status={
              currentStep > 2
                ? 'finish'
                : currentStep === 2
                  ? 'process'
                  : 'wait'
            }
          />
          <Step
            title="发送S&P"
            description="邮件发送"
            icon={<SendOutlined />}
            status={
              currentStep > 3
                ? 'finish'
                : currentStep === 3
                  ? 'process'
                  : 'wait'
            }
          />
          <Step
            title="S&P签署"
            description="邮件签署"
            icon={<MailOutlined />}
            status={currentStep === 4 ? 'finish' : 'wait'}
          />
        </Steps>
      </Card>

      {/* 当前状态 */}
      <Card title="当前状态" style={{ marginBottom: 24 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="EL状态">
                <UnifiedStatusTag type="elStatus" status={elStatus} />
              </Descriptions.Item>
              <Descriptions.Item label="进度">
                {elData?.progress || 0}%
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={8}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="S&P区域">
                {elData?.spRegion || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="S&P邮箱">
                {elData?.spAnalystEmail || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={8}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="费用">
                ${elData?.estimatedFee?.toLocaleString() || '-'}{' '}
                {elData?.currency || 'USD'}
              </Descriptions.Item>
              <Descriptions.Item label="模板">
                {elData?.elTemplateId || '-'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* Step 1: 生成委托协议 */}
      {elStatus === 'PENDING_GENERATION' && (
        <Card title="步骤1: 生成委托协议" style={{ marginBottom: 24 }}>
          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="S&P区域"
                  name="spRegion"
                  rules={[{ required: true, message: '请选择S&P区域' }]}
                >
                  <Select placeholder="请选择S&P区域">
                    <Option value="North America">North America</Option>
                    <Option value="Europe">Europe</Option>
                    <Option value="Asia Pacific">Asia Pacific</Option>
                    <Option value="Latin America">Latin America</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="S&P邮箱"
                  name="spAnalystEmail"
                  rules={[
                    { required: true, message: '请输入S&P邮箱' },
                    { type: 'email', message: '请输入有效的邮箱地址' },
                  ]}
                >
                  <Input placeholder="analyst@spglobal.com" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="协议模板"
                  name="elTemplateId"
                  rules={[{ required: true, message: '请选择协议模板' }]}
                  initialValue="standard-green-bond-spo"
                >
                  <Select placeholder="请选择协议模板">
                    <Option value="standard-green-bond-spo">
                      标准绿色债券SPO协议
                    </Option>
                    <Option value="comprehensive-esg-spo">
                      综合ESG SPO协议
                    </Option>
                    <Option value="climate-bond-spo">气候债券SPO协议</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="评估费用 (USD)"
                  name="estimatedFee"
                  rules={[{ required: true, message: '请输入评估费用' }]}
                  initialValue={15000}
                >
                  <Input type="number" prefix="$" placeholder="15000" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={handleGenerateEL}
                  loading={isGenerating}
                >
                  生成委托协议
                </Button>
                <Button onClick={() => form.resetFields()}>重置</Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      )}

      {/* Step 2-3: 签署委托协议 */}
      {(elStatus === 'BRAVOWHALE_SIGNED' ||
        elStatus === 'ISSUER_SIGNED' ||
        elStatus === 'SENT_TO_SP' ||
        elStatus === 'SIGNED_BY_SP') &&
        elData && (
          <Card title="委托协议详情" style={{ marginBottom: 24 }}>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* 文档操作 */}
              <div>
                <Title level={5}>文档操作</Title>
                <Space>
                  <Button icon={<EyeOutlined />} onClick={handlePreview}>
                    预览文档
                  </Button>
                  <Button icon={<DownloadOutlined />} onClick={handleDownload}>
                    下载文档
                  </Button>
                </Space>
              </div>

              <Divider />

              {/* 签署记录 */}
              <div>
                <Title level={5}>签署记录</Title>
                <Timeline>
                  {Object.entries(elData.signatures || {}).map(
                    ([role, sig]) => {
                      if (!sig) return null;
                      return (
                        <Timeline.Item
                          key={role}
                          color={
                            role === 'bravowhale'
                              ? 'blue'
                              : role === 'issuer'
                                ? 'green'
                                : 'purple'
                          }
                          dot={<CheckCircleOutlined />}
                        >
                          <p style={{ margin: 0, fontWeight: 600 }}>
                            {role === 'bravowhale'
                              ? 'Bravowhale'
                              : role === 'issuer'
                                ? '发行人'
                                : 'S&P Global'}
                            签署
                          </p>
                          <p
                            style={{
                              margin: 0,
                              color: '#666',
                              fontSize: '12px',
                            }}
                          >
                            签署人：{sig.signerName} ({sig.signerEmail})
                          </p>
                          <p
                            style={{
                              margin: 0,
                              color: '#666',
                              fontSize: '12px',
                            }}
                          >
                            时间：{new Date(sig.signedAt).toLocaleString()}
                          </p>
                        </Timeline.Item>
                      );
                    }
                  )}
                </Timeline>
              </div>

              <Divider />

              {/* 操作按钮 */}
              <div>
                <Title level={5}>可用操作</Title>
                <Space wrap>
                  {elStatus === 'BRAVOWHALE_SIGNED' && (
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={() => {
                        setSignatureType('issuer');
                        setShowSignModal(true);
                      }}
                    >
                      发行人签署
                    </Button>
                  )}

                  {elStatus === 'ISSUER_SIGNED' && (
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={() => setShowSendModal(true)}
                    >
                      发送给S&P
                    </Button>
                  )}

                  {elStatus === 'SENT_TO_SP' && (
                    <Button
                      type="primary"
                      icon={<MailOutlined />}
                      onClick={() => setShowRecordSPSignModal(true)}
                    >
                      记录S&P签署
                    </Button>
                  )}

                  {elStatus === 'SIGNED_BY_SP' && (
                    <Alert
                      message="委托协议已完成"
                      description="所有签署已完成，可以进入下一阶段：付款凭证上传"
                      type="success"
                      showIcon
                    />
                  )}
                </Space>
              </div>
            </Space>
          </Card>
        )}

      {/* 签署Modal */}
      <Modal
        title={`${signatureType === 'bravowhale' ? 'Bravowhale' : '发行人'}签署委托协议`}
        open={showSignModal}
        onOk={handleSignEL}
        onCancel={() => {
          setShowSignModal(false);
          setSignatureData(''); // 关闭时清空签名
        }}
        confirmLoading={isSigning}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Alert
            message="签署确认"
            description="请确认您已仔细阅读委托协议的所有条款，并同意签署"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="agreementConfirmed"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error('请确认同意协议条款')),
              },
            ]}
          >
            <Space>
              <input type="checkbox" />
              <span>我已阅读并同意委托协议的所有条款</span>
            </Space>
          </Form.Item>

          <Divider>手写签名</Divider>

          <SignaturePad
            onConfirm={data => {
              setSignatureData(data);
              message.success('签名已保存');
            }}
            onClear={() => setSignatureData('')}
            disabled={isSigning}
          />

          <Divider />

          <Paragraph type="secondary" style={{ fontSize: '12px' }}>
            签署后，协议将进入下一签署流程。请确保您有权代表
            {signatureType === 'bravowhale' ? 'Bravowhale运营商' : '发行人'}
            签署此协议。
          </Paragraph>
        </Form>
      </Modal>

      {/* 发送Modal */}
      <Modal
        title="发送委托协议给S&P"
        open={showSendModal}
        onOk={handleSendToSP}
        onCancel={() => setShowSendModal(false)}
        confirmLoading={isSending}
        width={500}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            message="确认发送"
            description={`系统将自动向S&P分析师（${elData?.spAnalystEmail || 'N/A'}）发送委托协议邮件，包含已签署的协议文档和项目相关信息。`}
            type="info"
            showIcon
          />

          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="收件人邮箱">
              {elData?.spAnalystEmail || 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="邮件主题">
              Engagement Letter for SPO Assessment - {spoData?.projectId}
            </Descriptions.Item>
            <Descriptions.Item label="协议费用">
              ${elData?.estimatedFee} {elData?.currency || 'USD'}
            </Descriptions.Item>
          </Descriptions>

          <Alert
            message="提示"
            description="发送后，S&P将通过邮件回复签署的协议。运营商需要在收到邮件后手动记录S&P的签署信息。"
            type="warning"
            showIcon
          />
        </Space>
      </Modal>

      {/* 记录S&P签署Modal */}
      <Modal
        title="记录S&P签署信息"
        open={showRecordSPSignModal}
        onOk={handleRecordSPSignature}
        onCancel={() => setShowRecordSPSignModal(false)}
        confirmLoading={isRecording}
      >
        <Form form={form} layout="vertical">
          <Alert
            message="邮件签署记录"
            description="S&P通过邮件发送签署的协议。请上传邮件附件或输入邮件链接作为签署凭证。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            label="S&P签署凭证URL"
            name="spSignatureProofUrl"
            rules={[{ required: true, message: '请输入S&P签署凭证URL' }]}
          >
            <Input placeholder="https://example.com/signed-el.pdf" />
          </Form.Item>

          <Form.Item
            label="S&P签署日期"
            name="spSignedDate"
            rules={[{ required: true, message: '请输入S&P签署日期' }]}
          >
            <Input type="datetime-local" />
          </Form.Item>

          <Form.Item label="上传邮件附件（可选）">
            <Upload
              accept=".pdf,.doc,.docx"
              maxCount={1}
              beforeUpload={() => false}
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
          </Form.Item>
        </Form>
      </Modal>

      {/* 文档预览Modal */}
      <Modal
        title="委托协议预览"
        open={showPreviewModal}
        onCancel={() => setShowPreviewModal(false)}
        footer={[
          <Button
            key="download"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
          >
            下载
          </Button>,
          <Button key="close" onClick={() => setShowPreviewModal(false)}>
            关闭
          </Button>,
        ]}
        width={800}
      >
        {elData?.elDocumentUrl ? (
          <iframe
            src={elData.elDocumentUrl}
            style={{ width: '100%', height: '600px', border: 'none' }}
            title="Engagement Letter Preview"
          />
        ) : (
          <Empty description="文档预览不可用" />
        )}
      </Modal>
    </div>
  );
};

export default EngagementLetterPage;
